import { Flex } from "antd";
import { useEffect, useState } from "react";
import type { GetPromotionResponse } from "../../../../app/features/courses/courses.types";
import useCopy from "../../../../hooks/useCopy";
import { addZero, enToBnNumber } from "../../../../utils/number.utils";
import CopyIcon from "../_icons/CopyIcon";

type Promotion = GetPromotionResponse["items"][number];

interface PromotionCardProps {
  promotion: Promotion;
}

type TimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
};

const getTimeLeft = (endDate: string): TimeLeft => {
  const end = new Date(endDate).getTime();
  const now = Date.now();
  const diff = end - now;

  if (Number.isNaN(end) || diff <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      isExpired: true,
    };
  }

  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return {
    days,
    hours,
    minutes,
    seconds,
    isExpired: false,
  };
};

export default function PromotionCard({ promotion }: PromotionCardProps) {
  const handleCopy = useCopy();
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
    getTimeLeft(promotion.endsAt),
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(getTimeLeft(promotion.endsAt));
    }, 1000);

    return () => clearInterval(interval);
  }, [promotion.endsAt]);

  const { days, hours, minutes, seconds, isExpired } = timeLeft;

  const renderTime = (value: number) => enToBnNumber(addZero(value));

  const discountLabel =
    promotion.discountKind === "PERCENT"
      ? `${promotion.value}%`
      : promotion.discountKind === "FIXED_AMOUNT"
        ? `৳${enToBnNumber(promotion.value)}`
        : "ফ্রি শিপিং";

  return (
    <article className="rounded-xl border border-theme-zinc-200 bg-white px-5 py-4 shadow-sm">
      <h3 className="text-theme-zinc-700 text-lg font-medium mb-5">
        {promotion.name}
      </h3>
      <Flex align="center" justify="space-between" gap={10}>
        <div className="px-3 py-[5px] bg-[#D2E5E5] rounded-lg inline-flex items-center gap-3">
          <span className="text-theme-zinc-700 text-sm font-roboto">
            {promotion.code}
          </span>
          <span
            className="cursor-pointer"
            onClick={() => handleCopy(promotion.code)}
          >
            <CopyIcon />
          </span>
        </div>

        <p className="text-theme-slate-400 text-sm font-">
          সর্বোচ্চ {enToBnNumber(discountLabel)} ছাড়
        </p>
      </Flex>

      <div className="mt-6 bg-[#E9F2F2] border border-[#90BFBE] px-7 py-3 rounded-lg">
        <h3 className="text-center text-theme-slate-700 text-sm leading-6 mb-4">
          অফার শেষ হবে
        </h3>

        {!isExpired && (
          <div className="flex justify-between gap-3">
            <TimeBox label="দিন" value={renderTime(days)} />
            <span className="text-theme-zinc-700 text-2xl font-semibold">
              :
            </span>
            <TimeBox label="ঘণ্টা" value={renderTime(hours)} />
            <span className="text-theme-zinc-700 text-2xl font-semibold">
              :
            </span>
            <TimeBox label="মিনিট" value={renderTime(minutes)} />
            <span className="text-theme-zinc-700 text-2xl font-semibold">
              :
            </span>
            <TimeBox label="সেকেন্ড" value={renderTime(seconds)} />
          </div>
        )}
      </div>
    </article>
  );
}

interface TimeBoxProps {
  label: string;
  value: string | number;
}

function TimeBox({ label, value }: TimeBoxProps) {
  return (
    <div className="flex flex-col items-center justify-center">
      <span className="text-theme-zinc-700 text-3xl font-semibold [text-shadow:_0px_3px_4px_rgb(0_0_0_/_0.38)]">
        {value}
      </span>
      <span className="mt-1 text-theme-zinc-700 text-xs">{label}</span>
    </div>
  );
}
